import { Scale, Shield, Building2, Landmark, FileText, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/lib/lang";

const AREAS = [
  { id: "civile", num: "01", icon: Scale },
  { id: "penale", num: "02", icon: Shield },
  { id: "societario", num: "03", icon: Building2 },
  { id: "immobiliare", num: "04", icon: Landmark },
  { id: "crediti", num: "05", icon: FileText },
  { id: "patrimonio", num: "06", icon: ShieldCheck },
];

const Card = ({ area, label, className, delay }) => {
  const Icon = area.icon;
  return (
    <Reveal delay={delay} className={`group relative overflow-hidden border border-hairline bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-[0_24px_60px_-30px_rgba(11,29,58,0.35)] ${className}`}>
      <div className="flex items-start justify-between">
        <Icon size={26} strokeWidth={1.1} className="text-gold-dark transition-colors duration-500 group-hover:text-gold" />
        <span className="font-mono text-[11px] tracking-[0.2em] text-navy/25">{area.num}</span>
      </div>
      <h3 className="mt-8 font-serif text-2xl font-semibold leading-tight text-navy">{label.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-ink">{label.desc}</p>
      <ul className="mt-5 space-y-1.5 md:max-h-0 md:overflow-hidden md:opacity-0 md:transition-all md:duration-500 md:group-hover:max-h-48 md:group-hover:opacity-100">
        {label.items.map((it) => (
          <li key={it} className="flex items-center gap-2.5 text-[13px] text-navy/70">
            <span className="inline-block h-1 w-1 rotate-45 bg-gold" aria-hidden="true"></span>
            {it}
          </li>
        ))}
      </ul>
      <ArrowUpRight
        size={18}
        strokeWidth={1.25}
        className="absolute bottom-6 right-6 text-gold opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100 md:-translate-x-1"
      />
    </Reveal>
  );
};

export default function PracticeAreas() {
  const { t } = useLang();
  return (
    <section data-testid="practice-areas-section" id="aree" className="relative bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Overline>{t.aree.overline}</Overline>
            <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {t.aree.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-md text-sm leading-relaxed text-muted-ink lg:text-base">{t.aree.intro}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-6">
          <Card area={AREAS[0]} label={t.aree.cards.civile} className="md:col-span-4" delay={0} />
          <Card area={AREAS[1]} label={t.aree.cards.penale} className="md:col-span-2" delay={0.05} />
          <Card area={AREAS[2]} label={t.aree.cards.societario} className="md:col-span-3" delay={0.1} />
          <Card area={AREAS[3]} label={t.aree.cards.immobiliare} className="md:col-span-3" delay={0.15} />
          <Card area={AREAS[4]} label={t.aree.cards.crediti} className="md:col-span-3" delay={0.1} />
          <Card area={AREAS[5]} label={t.aree.cards.patrimonio} className="md:col-span-3" delay={0.15} />
        </div>
      </div>
    </section>
  );
}
